import {
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import {
    getAddressType,
    getIncomeValue,
    getOccupation,
    getPepValue,
    getSourceOfWealth,
} from '../../portfolios/utils';
import enUs from '../../../../lang/en-us';
import { showHelperText, isError } from '../../commercial/Switch/utils';
import DatePickerInput from '../../../../lib/DatePicker';
import { checkForOnlyAlphabets, isFloatingNumber } from '../../../../utils/constants';
import { MAX_LENGTHS } from './utils';
import { useFlipMenuProps, FlipMenuStyles } from './useFlipMenu';

const { dashboard } = enUs;

const focusStyles = `
  /* ── Select / combobox focus ── */
  .kyc-select .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #1a56db;
    border-width: 2px;
  }
  .kyc-select .MuiSelect-select:focus {
    outline: 3px solid #1a56db;
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* ── TextField input focus ── */
  .kyc-textfield .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #1a56db;
    border-width: 2px;
  }
  .kyc-textfield input:focus {
    outline: 3px solid #1a56db;
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* ── DatePicker input focus ── */
  .kyc-datepicker input:focus {
    outline: 3px solid #1a56db;
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* ── WCAG 1.4.12 Text Spacing: Select trigger (selected value) ── */
  .kyc-select .MuiSelect-selectMenu,
  .kyc-select .MuiSelect-select {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    height: auto;
    min-height: 1.4375em;
    overflow-wrap: anywhere;
  }
  .kyc-select .MuiOutlinedInput-input {
    height: auto;
  }
  .kyc-select .MuiInputLabel-outlined {
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

export default function KycFields({ kycFormik }: any) {
    const pepFlip = useFlipMenuProps();
    const incomeFlip = useFlipMenuProps();
    const occupationFlip = useFlipMenuProps();
    const addressFlip = useFlipMenuProps();
    const sourceFlip = useFlipMenuProps();

    return (
        <div className="new-otm-fields" style={{ borderTop: '1px solid #dedbee', paddingTop: 25 }}>
            <style>{focusStyles}</style>
            <FlipMenuStyles />

            <h2
                style={{
                    fontSize: '0.67em',
                    fontWeight: 'bold',
                    margin: '2.33em 0',
                }}
            >
                {dashboard.FatcaDeclaration.kycFields.heading}
            </h2>

            <Grid container spacing={1}>
                {/* Father Name */}
                <Grid item xs={12} sm={5}>
                    {/*
                     * FIX: Added className="kyc-textfield" so the scoped focus
                     * style `.kyc-textfield input:focus` applies to this field.
                     */}
                    <TextField
                        className="kyc-textfield"
                        id="father-name"
                        name="fatherName"
                        variant="outlined"
                        value={kycFormik.values.fatherName}
                        helperText={showHelperText(kycFormik, 'fatherName')}
                        error={isError(kycFormik, 'fatherName')}
                        onChange={(e) => {
                            if (
                                checkForOnlyAlphabets(e.target.value) &&
                                e.target.value.length <= MAX_LENGTHS.fatherName
                            ) {
                                kycFormik.setFieldValue('fatherName', e.target.value);
                            }
                        }}
                        onBlur={kycFormik.handleBlur}
                        label={dashboard.FatcaDeclaration.kycFields.fatherName}
                    />
                </Grid>

                {/* Spouse Name */}
                <Grid item xs={12} sm={5}>
                    {/* FIX: Added className="kyc-textfield" for visible focus ring */}
                    <TextField
                        className="kyc-textfield"
                        id="spouse-name"
                        name="spouseName"
                        variant="outlined"
                        helperText={showHelperText(kycFormik, 'spouseName')}
                        error={isError(kycFormik, 'spouseName')}
                        onChange={(e) => {
                            if (
                                checkForOnlyAlphabets(e.target.value) &&
                                e.target.value.length <= MAX_LENGTHS.spouseName
                            ) {
                                kycFormik.setFieldValue('spouseName', e.target.value);
                            }
                        }}
                        value={kycFormik.values.spouseName}
                        onBlur={kycFormik.handleBlur}
                        label={dashboard.FatcaDeclaration.kycFields.spouseName}
                    />
                </Grid>

                {/* PEP */}
                <Grid item xs={12} sm={5}>
                    {/*
                     * FIX: Added className="kyc-select" on FormControl so the
                     * scoped focus styles apply to the MUI Select inside it.
                     * MUI Select's native focus ring is suppressed by MUI internals;
                     * the `.kyc-select .MuiSelect-select:focus` rule restores it.
                     */}

                    <FormControl
                        variant="outlined"
                        fullWidth
                        className="kyc-select"
                        ref={pepFlip.anchorRef}
                    >
                        <InputLabel id="pepFlag-label" htmlFor="pepFlag">
                            {dashboard.FatcaDeclaration.kycFields.pepFlag}
                        </InputLabel>

                        <Select
                            labelId="pepFlag-label"
                            id="pepFlag"
                            name="pepFlag"
                            value={kycFormik.values.pepFlag}
                            onChange={kycFormik.handleChange}
                            onOpen={pepFlip.handleOpen}
                            label={dashboard.FatcaDeclaration.kycFields.pepFlag}
                            MenuProps={pepFlip.menuProps}
                        >
                            {Object.keys(getPepValue).map((item: any) => (
                                <MenuItem key={item} value={item}>
                                    {getPepValue[item]}
                                </MenuItem>
                            ))}
                        </Select>

                        {kycFormik.errors.pepFlag && (
                            <FormHelperText
                                id="pepFlag-error"
                                error
                                style={{ color: 'red', fontSize: 12, padding: 4 }}
                                role="alert"
                            >
                                {showHelperText(kycFormik, 'pepFlag')}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Annual Income */}
                <Grid item xs={12} sm={5}>
                    {/* FIX: Added className="kyc-select" for visible focus ring */}
                    <FormControl
                        variant="outlined"
                        fullWidth
                        className="kyc-select"
                        ref={incomeFlip.anchorRef}
                    >
                        <InputLabel id="annualIncomeSlab-label" htmlFor="annualIncomeSlab">
                            {dashboard.FatcaDeclaration.kycFields.annualIncomeSlab}
                        </InputLabel>

                        <Select
                            labelId="annualIncomeSlab-label"
                            id="annualIncomeSlab"
                            name="annualIncomeSlab"
                            value={kycFormik.values.annualIncomeSlab}
                            onChange={kycFormik.handleChange}
                            onOpen={incomeFlip.handleOpen}
                            label={dashboard.FatcaDeclaration.kycFields.annualIncomeSlab}
                            MenuProps={incomeFlip.menuProps}
                            aria-invalid={!!kycFormik.errors.annualIncomeSlab}
                            aria-describedby={
                                kycFormik.errors.annualIncomeSlab
                                    ? 'annualIncomeSlab-error'
                                    : undefined
                            }
                        >
                            {Object.keys(getIncomeValue).map((item: any) => (
                                <MenuItem key={item} value={item}>
                                    {getIncomeValue[item]}
                                </MenuItem>
                            ))}
                        </Select>

                        {kycFormik.errors.annualIncomeSlab && (
                            <FormHelperText
                                id="annualIncomeSlab-error"
                                error
                                style={{ color: 'red', fontSize: 12, padding: 4 }}
                                role="alert"
                            >
                                {showHelperText(kycFormik, 'annualIncomeSlab')}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Occupation */}
                <Grid item xs={12} sm={5}>
                    {/* FIX: Added className="kyc-select" for visible focus ring */}
                    <FormControl
                        variant="outlined"
                        fullWidth
                        className="kyc-select"
                        ref={occupationFlip.anchorRef}
                    >
                        <InputLabel id="occupation-label" htmlFor="occupation">
                            {dashboard.FatcaDeclaration.kycFields.occupation}
                        </InputLabel>

                        <Select
                            labelId="occupation-label"
                            id="occupation"
                            name="occupation"
                            value={kycFormik.values.occupation}
                            onChange={kycFormik.handleChange}
                            onOpen={occupationFlip.handleOpen}
                            label={dashboard.FatcaDeclaration.kycFields.occupation}
                            MenuProps={occupationFlip.menuProps}
                            aria-invalid={!!kycFormik.errors.occupation}
                            aria-describedby={
                                kycFormik.errors.occupation ? 'occupation-error' : undefined
                            }
                        >
                            {Object.keys(getOccupation).map((item: any) => (
                                <MenuItem key={item} value={item}>
                                    {getOccupation[item]}
                                </MenuItem>
                            ))}
                        </Select>

                        {kycFormik.errors.occupation && (
                            <FormHelperText
                                id="occupation-error"
                                error
                                style={{ color: 'red', fontSize: 12, padding: 4 }}
                                role="alert"
                            >
                                {showHelperText(kycFormik, 'occupation')}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Address Type */}
                <Grid item xs={12} sm={5}>
                    {/* FIX: Added className="kyc-select" for visible focus ring */}
                    <FormControl
                        variant="outlined"
                        fullWidth
                        className="kyc-select"
                        error={isError(kycFormik, 'addressType')}
                        ref={addressFlip.anchorRef}
                    >
                        <InputLabel id="addressType-label" htmlFor="addressType">
                            {dashboard.FatcaDeclaration.kycFields.addressType}
                        </InputLabel>

                        <Select
                            labelId="addressType-label"
                            id="addressType"
                            name="addressType"
                            value={kycFormik.values.addressType}
                            onChange={kycFormik.handleChange}
                            onOpen={addressFlip.handleOpen}
                            label={dashboard.FatcaDeclaration.kycFields.addressType}
                            MenuProps={addressFlip.menuProps}
                            aria-invalid={!!kycFormik.errors.addressType}
                            aria-describedby={
                                kycFormik.errors.addressType ? 'addressType-error' : undefined
                            }
                        >
                            {Object.keys(getAddressType).map((item: any) => (
                                <MenuItem key={item} value={item}>
                                    {getAddressType[item]}
                                </MenuItem>
                            ))}
                        </Select>

                        {kycFormik.errors.addressType && (
                            <FormHelperText
                                id="addressType-error"
                                style={{ color: 'red', fontSize: 12, padding: 4 }}
                                role="alert"
                            >
                                {showHelperText(kycFormik, 'addressType')}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Source of Wealth */}
                <Grid item xs={12} sm={5}>
                    {/* FIX: Added className="kyc-select" for visible focus ring */}
                    <FormControl
                        variant="outlined"
                        fullWidth
                        className="kyc-select"
                        error={isError(kycFormik, 'sourceOfWealth')}
                        ref={sourceFlip.anchorRef}
                    >
                        <InputLabel id="sourceOfWealth-label" htmlFor="sourceOfWealth">
                            {dashboard.FatcaDeclaration.kycFields.sourceOfWealth}
                        </InputLabel>

                        <Select
                            labelId="sourceOfWealth-label"
                            id="sourceOfWealth"
                            name="sourceOfWealth"
                            value={kycFormik.values.sourceOfWealth}
                            onChange={kycFormik.handleChange}
                            onOpen={sourceFlip.handleOpen}
                            label={dashboard.FatcaDeclaration.kycFields.sourceOfWealth}
                            MenuProps={sourceFlip.menuProps}
                            aria-invalid={!!kycFormik.errors.sourceOfWealth}
                            aria-describedby={
                                kycFormik.errors.sourceOfWealth ? 'sourceOfWealth-error' : undefined
                            }
                        >
                            {Object.keys(getSourceOfWealth).map((item: any) => (
                                <MenuItem key={item} value={item}>
                                    {getSourceOfWealth[item]}
                                </MenuItem>
                            ))}
                        </Select>

                        {kycFormik.errors.sourceOfWealth && (
                            <FormHelperText
                                id="sourceOfWealth-error"
                                style={{ color: 'red', fontSize: 12, padding: 4 }}
                                role="alert"
                            >
                                {showHelperText(kycFormik, 'sourceOfWealth')}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Net Worth */}
                <Grid item xs={12} sm={5}>
                    {/* FIX: Added className="kyc-textfield" for visible focus ring */}
                    <TextField
                        className="kyc-textfield"
                        id="netWorth"
                        name="netWorth"
                        variant="outlined"
                        helperText={showHelperText(kycFormik, 'netWorth')}
                        error={isError(kycFormik, 'netWorth')}
                        onChange={(e) => {
                            if (
                                isFloatingNumber(e.target.value) &&
                                e.target.value.length <= MAX_LENGTHS.netWorth
                            ) {
                                kycFormik.setFieldValue('netWorth', e.target.value);
                            }
                        }}
                        value={kycFormik.values.netWorth}
                        onBlur={kycFormik.handleBlur}
                        label={dashboard.FatcaDeclaration.kycFields.netWorth}
                    />
                </Grid>

                {/* Net Worth Date */}
                <Grid item xs={12} sm={5}>
                    {/*
                     * FIX: Wrapped DatePickerInput in a div with className="kyc-datepicker"
                     * so the scoped `.kyc-datepicker input:focus` rule restores the
                     * focus ring on the date input, which MUI also strips by default.
                     */}
                    <div className="kyc-datepicker">
                        <DatePickerInput
                            label={dashboard.FatcaDeclaration.kycFields.netWorthDate}
                            name="netWorthDate"
                            value={kycFormik.values.netWorthDate}
                            disableFuture={true}
                            onChange={(e) => {
                                kycFormik.setFieldValue('netWorthDate', e);
                            }}
                        />
                    </div>
                </Grid>
            </Grid>
        </div>
    );
}
